<template>
  <BaseModal
    :value="true"
    @input="$emit('close')"
    id="web-address-phone-selector"
  >
    <template v-slot:header>{{ modalTitle }}</template>
    <div class="modal-wrapper">
      <div class="info-banner">
        <svg
          class="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        <span>{{ infoBannerText }}</span>
      </div>

      <button
        v-if="hasDisabledItems"
        class="show-disabled-toggle"
        :class="{ active: showDisabled }"
        @click="showDisabled = !showDisabled"
      >
        <i class="mdi" :class="showDisabled ? 'mdi-eye-off' : 'mdi-eye'"></i>
        {{ showDisabled ? "Hide disabled" : "Show disabled" }}
      </button>

      <div v-if="loading" class="h-60 flex items-center justify-center">
        <baseMiniSpinner></baseMiniSpinner>
      </div>

      <div v-else class="flex flex-col gap-4">
        <!-- Infoauto Addresses Section -->
        <div
          class="section-box"
          v-if="
            shouldShowAddresses &&
              infoautoAddresses &&
              infoautoAddresses.length > 0
          "
        >
          <h2 class="section-title">Infoauto Addresses</h2>
          <div class="flex flex-row flex-wrap gap-2">
            <div
              v-for="address in infoautoAddresses"
              :key="address.infoautoAddressId"
              class="p-4 border rounded-md relative address-card"
              :class="{
                'address-selectable': canSelectBoth,
                'disabled-card': address.disabled === 1,
                'is-current': isCurrentAddress(address)
              }"
              @click="canSelectBoth ? selectAddress(address) : null"
            >
              <div v-if="isCurrentAddress(address)" class="current-badge">
                <i class="mdi mdi-check-circle"></i>
                {{ $t("message.order.current_in_order") }}
              </div>
              <div class="address-badge infoauto-badge">Infoauto</div>

              <!-- Use InfoautoAddressDisplay component -->
              <InfoautoAddressDisplay
                :address="address"
                :showResidential="true"
                :showStatus="false"
              />

              <button
                v-if="canSelectAddress && !isCurrentAddress(address)"
                @click.stop="selectAddress(address)"
                class="select-button"
              >
                <i class="mdi mdi-check-circle"></i>
                Select Address
              </button>
            </div>
          </div>
        </div>

        <!-- Web Addresses Section -->
        <div
          class="section-box"
          v-if="shouldShowAddresses && webAddresses && webAddresses.length > 0"
        >
          <h2 class="section-title">Web Addresses</h2>
          <div class="flex flex-row flex-wrap gap-2">
            <div
              v-for="(address, index) in webAddresses"
              :key="index"
              class="p-4 border rounded-md relative address-card"
              :class="{
                'address-selectable': canSelectBoth,
                'is-current': isCurrentAddress(address)
              }"
              @click="canSelectBoth ? selectAddress(address) : null"
            >
              <div v-if="isCurrentAddress(address)" class="current-badge">
                <i class="mdi mdi-check-circle"></i>
                {{ $t("message.order.current_in_order") }}
              </div>
              <div class="address-badge web-badge">Web</div>
              <p class="font-semibold">{{ address.name }}</p>
              <p v-if="address.company">{{ address.company }}</p>
              <p>{{ address.street1 }}</p>
              <p v-if="address.street2">{{ address.street2 }}</p>
              <p>
                {{ address.city }}, {{ address.state }} {{ address.postalCode }}
              </p>
              <p>{{ address.country }}</p>
              <p v-if="address.phone" class="text-blue-600 font-medium mt-2">
                📞 {{ address.phone }}
              </p>
              <p class="text-sm text-gray-600 mt-2">
                {{ address.residential === "1" ? "Residential" : "Commercial" }}
              </p>
              <button
                v-if="canSelectAddress && !isCurrentAddress(address)"
                @click.stop="selectAddress(address)"
                class="select-button"
              >
                <i class="mdi mdi-check-circle"></i>
                Select Address
              </button>
            </div>
          </div>
        </div>

        <!-- Phones Section (Infoauto Only) -->
        <div
          class="section-box"
          v-if="shouldShowPhones && infoautoPhones && infoautoPhones.length > 0"
        >
          <h2 class="section-title">Available Phones</h2>
          <div class="flex flex-row flex-wrap gap-2">
            <div
              v-for="phone in infoautoPhones"
              :key="phone.infoautoPhoneId"
              class="p-4 border rounded-md relative phone-card"
              :class="{
                'phone-selectable': canSelectBoth,
                'disabled-card': phone.disabled === 1,
                'is-current': isCurrentPhone(phone)
              }"
              @click="canSelectBoth ? selectPhone(phone) : null"
            >
              <div v-if="isCurrentPhone(phone)" class="current-badge">
                <i class="mdi mdi-check-circle"></i>
                {{ $t("message.order.current_in_order") }}
              </div>
              <div class="phone-content">
                <div class="phone-number">
                  <svg
                    class="w-5 h-5 text-blue-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                    />
                  </svg>
                  <span class="text-lg font-semibold">
                    {{ phone.areaCode ? `(${phone.areaCode}) ` : ""
                    }}{{ phone.number }}
                  </span>
                </div>
              </div>
              <button
                v-if="canSelectPhone && !isCurrentPhone(phone)"
                @click.stop="selectPhone(phone)"
                class="select-button"
              >
                <i class="mdi mdi-phone-check"></i>
                Select Phone
              </button>
            </div>
          </div>
        </div>

        <!-- Empty State -->
        <div v-if="showEmptyState" class="text-center p-8 text-gray-500">
          <svg
            v-if="selectionMode === 'phone-only'"
            class="w-16 h-16 mx-auto mb-4 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
            />
          </svg>
          <svg
            v-else
            class="w-16 h-16 mx-auto mb-4 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
            />
          </svg>
          <p class="text-lg font-medium">
            {{
              selectionMode === "phone-only"
                ? "No phones found"
                : "No addresses found"
            }}
          </p>
          <p class="text-sm">
            {{
              selectionMode === "phone-only"
                ? "This customer has no saved phone numbers yet."
                : "This customer has no saved addresses yet."
            }}
          </p>
        </div>
      </div>
    </div>
  </BaseModal>
</template>

<script>
import InfoautoAddressDisplay from "@/components/shared/InfoautoAddressDisplay.vue";
import {
  isOrderAddress,
  isOrderPhone,
  orderFirst
} from "@/utils/order-address-match";

export default {
  components: {
    InfoautoAddressDisplay
  },
  props: {
    customer: {
      type: Object,
      default: null
    },
    customerInfoauto: {
      type: Object,
      default: null
    },
    // The order's address (with its phone): marked and listed first
    currentAddress: {
      type: Object,
      default: null
    },
    // New prop to control selection mode
    // 'both' - click on card to select (legacy behavior)
    // 'separate' - show separate buttons for address and phone
    // 'address-only' - only show address select buttons
    // 'phone-only' - only show phone select buttons
    selectionMode: {
      type: String,
      default: "both",
      validator: value =>
        ["both", "separate", "address-only", "phone-only"].includes(value)
    }
  },
  data() {
    return {
      loading: false,
      showDisabled: false
    };
  },
  computed: {
    webAddresses() {
      return orderFirst(this.customer?.address || [], this.isCurrentAddress);
    },
    infoautoAddresses() {
      if (!this.customerInfoauto?.address) return [];
      const addresses = this.showDisabled
        ? this.customerInfoauto.address
        : this.customerInfoauto.address.filter(addr => addr.disabled !== 1);
      return orderFirst(addresses, this.isCurrentAddress);
    },
    infoautoPhones() {
      if (!this.customerInfoauto?.phone) return [];
      const phones = this.showDisabled
        ? this.customerInfoauto.phone
        : this.customerInfoauto.phone.filter(phone => phone.disabled !== 1);
      return orderFirst(phones, this.isCurrentPhone);
    },
    hasDisabledItems() {
      const disabledAddresses = (this.customerInfoauto?.address || []).some(
        a => a.disabled === 1
      );
      const disabledPhones = (this.customerInfoauto?.phone || []).some(
        p => p.disabled === 1
      );
      return disabledAddresses || disabledPhones;
    },
    canSelectBoth() {
      return this.selectionMode === "both";
    },
    canSelectAddress() {
      return (
        this.selectionMode === "separate" ||
        this.selectionMode === "address-only"
      );
    },
    canSelectPhone() {
      return (
        this.selectionMode === "separate" || this.selectionMode === "phone-only"
      );
    },
    modalTitle() {
      if (this.selectionMode === "address-only") return "Select Address";
      if (this.selectionMode === "phone-only") return "Select Phone";
      return "Select Address & Phone";
    },
    infoBannerText() {
      if (this.selectionMode === "both") {
        return "Click on any address or phone to select it";
      } else if (this.selectionMode === "address-only") {
        return "Click the 'Select Address' button on the address you want to use";
      } else if (this.selectionMode === "phone-only") {
        return "Click the 'Select Phone' button on the phone number you want to use";
      } else {
        return "Click the button on the address or phone you want to select";
      }
    },
    shouldShowAddresses() {
      return (
        this.selectionMode === "both" ||
        this.selectionMode === "separate" ||
        this.selectionMode === "address-only"
      );
    },
    shouldShowPhones() {
      return (
        this.selectionMode === "both" ||
        this.selectionMode === "separate" ||
        this.selectionMode === "phone-only"
      );
    },
    showEmptyState() {
      if (this.selectionMode === "phone-only") {
        return !this.infoautoPhones || this.infoautoPhones.length === 0;
      }
      // For address modes
      return (
        (!this.webAddresses || this.webAddresses.length === 0) &&
        (!this.infoautoAddresses || this.infoautoAddresses.length === 0)
      );
    }
  },
  methods: {
    isCurrentAddress(address) {
      return isOrderAddress(address, this.currentAddress);
    },
    isCurrentPhone(phone) {
      return isOrderPhone(phone, this.currentAddress);
    },
    selectAddress(address) {
      this.$emit("address-selected", address);
    },
    selectPhone(phone) {
      this.$emit("phone-selected", phone);
    }
  }
};
</script>

<style lang="postcss" scoped>
#web-address-phone-selector {
  /* .modal-content es de BaseModal: sin ::v-deep el scoped no le llegaba */
  ::v-deep > .modal-content {
    width: 95%;
    max-width: 1200px;
    height: 90vh;
    overflow-y: auto;
  }

  /* Movil: pantalla completa y un cerrar del tamano de un dedo */
  @media (max-width: 767px) {
    padding: 0;

    ::v-deep > .modal-content {
      width: 100%;
      height: 100%;
      max-height: none;
      border-radius: 0;

      > .header {
        padding: 6px 52px 6px 12px;
        font-size: 15px;
        font-weight: 600;
      }

      > .header .close {
        top: 50%;
        right: 4px;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 44px;
        height: 44px;
        padding: 0;
        transform: translateY(-50%);
        font-size: 32px;
        color: #374151;
      }
    }
  }
}

.modal-wrapper {
  padding: 1rem;

  @media (max-width: 767px) {
    padding: 0.5rem 0.625rem 1rem;
  }
}

/* La direccion o el telefono que ya tiene la orden */
.address-card.is-current,
.phone-card.is-current {
  border: 2px solid #16a34a;
  background: #f0fdf4;
}

/* En el flujo de la tarjeta (arriba a la izquierda, frente a la etiqueta
   Infoauto/Web): absoluta tapaba el primer renglon */
.current-badge {
  display: inline-flex;
  align-self: flex-start;
  align-items: center;
  gap: 0.25rem;
  margin: -1.75rem 0 0.5rem;
  padding: 0.25rem 0.625rem;
  border-radius: 9999px;
  background: #16a34a;
  color: white;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
}

.phone-card .current-badge {
  margin-top: 0;
}

@media (max-width: 767px) {
  .info-banner {
    padding: 0.375rem 0.625rem;
    margin-bottom: 0.5rem;
    font-size: 14px;
  }

  .section-box {
    padding: 0;
    border: none;
  }

  .section-title {
    margin-bottom: 0.5rem;
    font-size: 1.125rem;
  }

  .address-card,
  .phone-card {
    min-width: 0;
    max-width: none;
    padding-right: 0.75rem;
    padding-left: 0.75rem;
  }
}

.info-banner {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  background-color: #dbeafe;
  border-left: 4px solid #3b82f6;
  padding: 1rem;
  margin-bottom: 1rem;
  border-radius: 0.375rem;
  color: #1e40af;
  font-weight: 500;
}

.show-disabled-toggle {
  @apply px-3 py-1.5 text-sm font-medium rounded-md border mb-3;
  @apply flex items-center gap-1.5 transition-all duration-200;
  background: white;
  color: #6b7280;
  border-color: #d1d5db;
  cursor: pointer;

  &:hover {
    background: #f3f4f6;
    color: #374151;
  }

  &.active {
    background: #fef3c7;
    color: #92400e;
    border-color: #fbbf24;
  }

  i {
    font-size: 16px;
  }
}

.disabled-card {
  opacity: 0.5;
  border-style: dashed !important;
  border-color: #d1d5db !important;
  background: #f9fafb;
}

.section-box {
  @apply p-4 border rounded-md bg-white;
}

.section-title {
  @apply font-bold text-xl text-black mb-4;
}

.address-card {
  position: relative;
  min-width: 280px;
  max-width: 320px;
  width: 100%;
  padding-top: 2.5rem;
}

.address-card p {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.address-badge {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
}

.web-badge {
  @apply bg-purple-100 text-purple-700;
}

.infoauto-badge {
  @apply bg-green-100 text-green-700;
}

.address-selectable,
.phone-selectable {
  cursor: pointer;
  transition: all 0.2s;
}

.address-selectable:hover,
.phone-selectable:hover {
  @apply bg-blue-50 border-blue-400;
  transform: translateY(-2px);
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.phone-card {
  position: relative;
  min-width: 250px;
  max-width: 320px;
  width: 100%;
}

.phone-content {
  @apply flex flex-col gap-2;
}

.phone-number {
  @apply flex items-center gap-2;
}

.select-button {
  @apply mt-3 w-full py-2 px-4 bg-blue-600 text-white font-medium rounded-md;
  @apply flex items-center justify-center gap-2;
  @apply transition-all duration-200;
  @apply hover:bg-blue-700 hover:shadow-md;
  cursor: pointer;

  &:active {
    transform: scale(0.95);
  }

  i {
    font-size: 18px;
  }
}

.address-card,
.phone-card {
  @apply flex flex-col;

  .select-button {
    margin-top: auto;
  }
}
</style>
